import {
  useRef,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useParams } from 'react-router-dom';

import { FormAddButton, FormDeleteButton } from 'common/components';

import { QaGridEdit } from 'pages/qa-spec/components';
import { WithQaGridHeader } from 'pages/qa-spec/components';

import { useGetProductEnums } from 'hooks';
import {
  useGetFormulaGridHandlers,
  useGetFormulaColDefs,
} from 'pages/qa-spec/hooks/useFormula';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';

import { getAllRows } from 'pages/qa-spec/utils';

const QaFormulaGrid = forwardRef((props, forwardedRef) => {
  const gridInst = useRef();
  const { id: productId } = useParams();

  const { title, isEdit, formulaGridData, snapshotGridValues, useSnapshot } =
    props;

  const [selectedRows, setSelectedRows] = useState([]);

  const { productEnums } = useGetProductEnums();

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const fields = useGetFormulaColDefs(productEnums, productId, { useSnapshot });

  const qasRequiredFieldMessage = useMemo(() => {
    return fields
      .map((column) => {
        if (!requiredFields?.length) return null;

        const isRequired = requiredFields.includes(
          `qaSpecification.qaSpecFormula.${column.field}`.toLowerCase()
        );

        if (!isRequired) return null;

        const displayName = column.displayName;
        return `${displayName} is required`;
      })
      .filter(Boolean);
  }, [requiredFields, JSON.stringify(fields)]);

  const displayNameMaps = fields.reduce((accumulator, fieldItem) => {
    const fieldName = fieldItem?.field;
    const displayName = fieldItem?.displayName;

    if (fieldName) accumulator[fieldName] = displayName;

    return accumulator;
  }, {});

  const {
    handleAddFormula,
    handleDeleteFormula,
    validateAllRow,
    checkGridError,
  } = useGetFormulaGridHandlers({
    gridInst,
    displayNameMaps,
    qasRequiredFieldMessage,
  });

  const rowClassRules = useMemo(
    () => ({
      'ag-row--error': (params) => {
        return !!params?.data?.error?.length > 0;
      },
    }),
    []
  );

  const getGridData = () => {
    return getAllRows(gridInst).map((formulaItem) => {
      return {
        ...formulaItem,
        country: formulaItem?.country?.length > 0 ? formulaItem?.country : null,
        digitalAssetId: formulaItem?.documents?.digitalAssetId,
      };
    });
  };

  const stopEditing = () => {
    gridInst.current.api.stopEditing();
  };

  const onCellEditingStopped = () => {
    validateAllRow();
  };

  const onRowSelected = () => {
    const selectedRows = gridInst.current.api.getSelectedNodes();
    setSelectedRows(selectedRows);
  };

  useImperativeHandle(forwardedRef, () => ({
    getGridData: () => {
      return getGridData();
    },
    updateGridData: (data) => {
      gridInst.current.api.setRowData(data);
    },
    stopEditing: () => {
      stopEditing();
    },
    clearSelectedRows: () => {
      setSelectedRows([]);
    },
    checkGridError: () => {
      return checkGridError();
    },
  }));

  return (
    <WithQaGridHeader
      title={title}
      forceEdit={isEdit}
      showSaveBtn={false}
      showCancelBtn={false}
      showEditBtn={false}
      buttons={[
        () => (
          <FormAddButton text='Add new formula' onClick={handleAddFormula} />
        ),
        () => (
          <FormDeleteButton
            disabled={selectedRows?.length === 0}
            onClick={handleDeleteFormula}
          />
        ),
      ]}
      productId={productId}
    >
      {(isEdit) => {
        return (
          <QaGridEdit
            fields={fields}
            isEdit={isEdit}
            gridInst={gridInst}
            gridData={formulaGridData}
            rowClassRules={rowClassRules}
            getRowNodeId={(data) => {
              return data?.['uuid'];
            }}
            onRowSelected={onRowSelected}
            onCellEditingStopped={onCellEditingStopped}
            snapshotGridValues={snapshotGridValues}
          />
        );
      }}
    </WithQaGridHeader>
  );
});

export default QaFormulaGrid;
